import React from 'react';
import { Box, Button, Flex, Heading, SegmentedControl, Text, Separator } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { uk } from 'date-fns/locale';

import APNavBar from 'Components/adminpanel/Elements/APNavBar';
import APSidebar from 'Components/adminpanel/APSidebar/APSidebar';
import { BusyIndicator } from 'shared/components';
import {
   MainAnalyticsChartContext,
   MainAnalyticsStore,
} from './main-analytics.store';
import {
   MainAnalyticsData,
   MainAnalyticsField,
   MainAnalyticsRegion,
   MainAnalyticsTopCategory,
} from './main-analytics.types';
import { useMainAnalytics } from './main-analytics.provider';
import { StatInfoCard } from './components/stat-info-card';
import { ActivityLineChart, BarChart, DoughnutChart } from './components/charts';

import './main-analytics.scss';

const contextLabels: Record<MainAnalyticsChartContext, string> = {
   cv: 'Резюме',
   simple_cv: 'Анкети',
   registrations: 'Компанії',
   jobs: 'Вакансії',
};

const getTopItems = <TItem extends MainAnalyticsField | MainAnalyticsRegion | MainAnalyticsTopCategory>(
   items: TItem[],
   valueKey: keyof TItem,
   limit = 5
) => [...items]
   .sort((a, b) => Number(b[valueKey]) - Number(a[valueKey]))
   .slice(0, limit);

const DashboardDateRangeFilter = observer(({ store }: { store: MainAnalyticsStore }) => {
   return (
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
         <Flex className="main-analytics__date-filter" align="center" justify="start" gap="3">
            <Box className="main-analytics__date-range">
               <DatePicker
                  format="dd-MM-yyyy"
                  maxDate={store.dateTo ?? undefined}
                  value={store.dateFrom}
                  onChange={(newValue, context) => {
                     if (context.validationError) {
                        return;
                     }

                     store.setDateFrom(newValue);
                  }}
                  slotProps={{
                     textField: {
                        placeholder: 'From',
                        className: 'main-analytics__date-field',
                     },
                  }}
               />
               <Text className="main-analytics__date-separator">-</Text>
               <DatePicker
                  format="dd-MM-yyyy"
                  minDate={store.dateFrom ?? undefined}
                  value={store.dateTo}
                  onChange={(newValue, context) => {
                     if (context.validationError) {
                        return;
                     }

                     store.setDateTo(newValue);
                  }}
                  slotProps={{
                     textField: {
                        placeholder: 'To',
                        className: 'main-analytics__date-field',
                     },
                  }}
               />
            </Box>

            <Button
               size="3"
               color="red"
               className="main-analytics__date-apply"
               disabled={store.isLoading}
               onClick={() => store.loadAnalytics()}
            >
               Apply
            </Button>
         </Flex>
      </LocalizationProvider>
   );
});

const SummaryCards = ({ data }: { data: MainAnalyticsData }) => (
   <Box className="main-analytics__stats-grid">
      <StatInfoCard
         title="Створено резюме"
         total={data.cv.total}
         details={[
            { label: 'Оригінальні резюме', value: data.cv.original },
            { label: 'Оновлено резюме', value: data.cv.updated },
         ]}
         tone="blue"
         icon={DescriptionOutlinedIcon}
      />
      <StatInfoCard
         title="Короткі анкети"
         total={data.simple_cv.total}
         details={[
            { label: 'З сайту', value: data.simple_cv.website },
            { label: 'З FB', value: data.simple_cv.fb },
         ]}
         tone="orange"
         icon={GroupAddOutlinedIcon}
      />
      <StatInfoCard
         title="Реєстрації"
         total={data.registrations.total}
         details={[
            { label: 'Овнери компаній', value: data.registrations.companies },
            { label: 'Менеджери', value: data.registrations.managers },
            { label: 'Реєстрація шукачів', value: data.registrations.jobseekers },
         ]}
         tone="teal"
         icon={BusinessCenterOutlinedIcon}
      />
      <StatInfoCard
         title="Вакансії та взаємодія"
         total={data.jobs.total}
         details={[
            { label: 'З них активних', value: data.jobs.active },
            { label: 'Відгуків', value: data.jobs.job_apply },
         ]}
         tone="green"
         icon={HandymanOutlinedIcon}
      />
   </Box>
);

const AnalyticsCharts = observer(({ store, data }: { store: MainAnalyticsStore; data: MainAnalyticsData }) => {
   const topFields = getTopItems(data.demographics.fields, 'total_count');
   const topRegions = getTopItems(data.demographics.region, 'count');
   const topCategories = getTopItems(data.top_categories, 'jobs_count');

   return (
      <Box className="main-analytics__workspace">
         <Box className="main-analytics__panel main-analytics__panel--demographics">
            <Heading as="h2" size="4" className="main-analytics__panel-title">
               Демографія шукачів
            </Heading>
            <Box className="main-analytics__donut-row">
               <Box className="main-analytics__chart main-analytics__chart--donut">
                  <DoughnutChart
                     label="Вікові групи"
                     labels={data.demographics.age_groups.map(
                        (item) => item.age_range ?? 'Не вказано'
                     )}
                     values={data.demographics.age_groups.map((item) => item.count)}
                  />
               </Box>
               <Box className="main-analytics__divider" />
               <Box className="main-analytics__chart main-analytics__chart--donut">
                  <DoughnutChart
                     label="Регіони шукачів"
                     labels={topRegions.map((item) => item.region_name)}
                     values={topRegions.map((item) => item.count)}
                     colors={['#2c9c9c', '#1976d2', '#dd6b20', '#2f9e44', '#8b5cf6']}
                  />
               </Box>
            </Box>
         </Box>

         <Box className="main-analytics__panel main-analytics__panel--segments">
            <Heading as="h2" size="4" className="main-analytics__panel-title">
               Сегменти індустрій роботодавців
            </Heading>
            <Box className="main-analytics__chart main-analytics__chart--bar">
               <BarChart
                  label="Вакансії"
                  labels={topCategories.map((item) => item.name)}
                  values={topCategories.map((item) => item.jobs_count)}
               />
            </Box>
            <Box className="main-analytics__divider" />
            <Box className="main-analytics__chart main-analytics__chart--bar">
               <BarChart
                  label="Кандидати"
                  labels={topFields.map((item) => item.name)}
                  values={topFields.map((item) => item.total_count)}
                  colors={['#1976d2', '#dd6b20', '#2c9c9c', '#2f9e44', '#8b5cf6']}
               />
            </Box>
         </Box>

         <Box className="main-analytics__panel main-analytics__panel--activity">
            <Flex justify="between" align="center" gap="3" className="main-analytics__chart-header">
               <Text size="2">Context</Text>
               <SegmentedControl.Root
                  size="2"
                  value={store.chartContext}
                  onValueChange={(value) =>
                     store.setChartContext(value as MainAnalyticsChartContext)
                  }
               >
                  {Object.entries(contextLabels).map(([value, label]) => (
                     <SegmentedControl.Item key={value} value={value}>
                        {label}
                     </SegmentedControl.Item>
                  ))}
               </SegmentedControl.Root>
            </Flex>
            <Box className="main-analytics__chart main-analytics__chart--line">
               <ActivityLineChart data={store.chartData} context={store.chartContext} />
            </Box>
            <Box className="main-analytics__insights">
               <Text>Пік створення резюме: {store.chartData[0]?.name ?? '-'}</Text>
               <Text>Поточний контекст: {contextLabels[store.chartContext]}</Text>
            </Box>
         </Box>
      </Box>
   );
});

const AnalyticsContent = observer(({ store }: { store: MainAnalyticsStore }) => {
   const data = store.data;

   if (store.isLoading) {
      return (
         <Flex className="main-analytics__state" align="center" justify="center" direction="column" gap="3">
            <BusyIndicator />
            <Text color="gray">Loading analytics</Text>
         </Flex>
      );
   }

   if (store.isError || !data) {
      return (
         <Flex className="main-analytics__state" align="center" justify="center" direction="column" gap="3">
            <Text color="red">Could not load analytics.</Text>
            <Button size="2" onClick={() => store.loadAnalytics()}>
               Retry
            </Button>
         </Flex>
      );
   }

   return (
      <>
         <SummaryCards data={data} />
         <Separator size="4" my="4"/>
         <DashboardDateRangeFilter store={store} />
         <AnalyticsCharts store={store} data={data} />
      </>
   );
});

const MainAnalyticsView = () => {
   const mainAnalytics = useMainAnalytics();

   React.useEffect(() => {
      mainAnalytics.loadAnalytics();
      window.scrollTo(0, 0);
   }, [mainAnalytics]);

   return (
      <>
         <APNavBar />
         <Box pt="80px" className="main-analytics">
            <Flex width="270px" minWidth="270px">
               <APSidebar />
            </Flex>

            <Box className="main-analytics__content">
               <Box className="main-analytics__header">
                  <Heading as="h1" size="7" weight="bold">
                     Dashboard
                  </Heading>
                  <Text as="p" className="main-analytics__subtitle">
                     Панель керування: Загальний огляд всіх показників рекрутингу
                  </Text>
               </Box>
               <AnalyticsContent store={mainAnalytics} />
            </Box>
         </Box>
      </>
   );
};

export const FeatureMainAnalyticsView = observer(MainAnalyticsView);
